import { useState } from "react";
import api from "../api";
import { markDeclared, logout } from "../auth";
import { toast } from "react-toastify";

// Shown by the jury route guard, not by the login page — the token exists before
// this renders, so gating on login alone was skippable with a refresh.
const DECLARATION_POINTS = [
  "I will evaluate nominations independently without external influence.",
  "I will assess entries only on defined award criteria and merit.",
  "I will not consider commercial relationships, prescriptions, or brand usage.",
  "I will maintain confidentiality of all evaluation and selection processes.",
  "I confirm compliance with applicable ethical and regulatory standards.",
  "I confirm that I currently have no Conflict of Interest (COI) and will promptly disclose any that may arise in the future.",
];

export default function DeclarationModal({ juryName, onAccept, onDecline }) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(15,23,42,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, backdropFilter: "blur(4px)",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16,
        width: "100%", maxWidth: 560,
        maxHeight: "92vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
        animation: "fadeIn 0.25s ease",
      }}>

        {/* Header */}
        <div style={{
          padding: "22px 28px 18px",
          borderBottom: "1px solid #F1F5F9",
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
          borderRadius: "16px 16px 0 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: "rgba(29,154,236,0.2)",
              border: "1px solid rgba(29,154,236,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20,
            }}>⚖️</div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>
                Jury Declaration
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748B" }}>
                Welcome, <strong style={{ color: "#94A3B8" }}>{juryName}</strong> — please read and confirm before proceeding
              </p>
            </div>
          </div>
        </div>

        {/* Declaration points */}
        <div style={{ overflowY: "auto", flex: 1, padding: "22px 28px" }}>
          <p style={{ margin: "0 0 18px", fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
            Before participating in the evaluation process, please confirm the following:
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {DECLARATION_POINTS.map((point, i) => (
              <div key={i} style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "12px 16px",
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 10,
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: "linear-gradient(135deg, #1D9AEC, #6366F1)",
                  color: "#fff", fontSize: 11, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 1,
                }}>{i + 1}</div>
                <p style={{ margin: 0, fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                  {point}
                </p>
              </div>
            ))}
          </div>

          {/* Checkbox */}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            marginTop: 22, cursor: "pointer",
            padding: "14px 16px",
            background: agreed ? "#F0FDF4" : "#FFFBEB",
            border: `1.5px solid ${agreed ? "#A7F3D0" : "#FDE68A"}`,
            borderRadius: 10,
            transition: "all 0.2s",
          }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 1, accentColor: "#10B981", flexShrink: 0, cursor: "pointer" }}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: agreed ? "#065F46" : "#92400E", lineHeight: 1.5 }}>
              I have read, understood, and agree to all the above declarations. I confirm my commitment to ethical and independent evaluation.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 28px",
          borderTop: "1px solid #F1F5F9",
          display: "flex", gap: 12, justifyContent: "flex-end",
          background: "#FAFAFA",
          borderRadius: "0 0 16px 16px",
        }}>
          <p style={{ flex: 1, margin: 0, fontSize: 11.5, color: "#94A3B8", alignSelf: "center", lineHeight: 1.5 }}>
            This declaration is required before each evaluation session.
          </p>
          {onDecline && (
            <button
              type="button"
              onClick={onDecline}
              style={{
                padding: "10px 18px", background: "#fff", color: "#64748B",
                border: "1px solid #E2E8F0", borderRadius: 9,
                fontWeight: 600, fontSize: 13.5, cursor: "pointer",
                whiteSpace: "nowrap", fontFamily: "inherit",
              }}
            >
              Sign out
            </button>
          )}
          <button
            onClick={onAccept}
            disabled={!agreed}
            style={{
              padding: "10px 24px",
              background: agreed
                ? "linear-gradient(135deg, #10B981, #059669)"
                : "#E2E8F0",
              color: agreed ? "#fff" : "#94A3B8",
              border: "none", borderRadius: 9,
              fontWeight: 700, fontSize: 14,
              cursor: agreed ? "pointer" : "not-allowed",
              boxShadow: agreed ? "0 4px 12px rgba(16,185,129,0.35)" : "none",
              transition: "all 0.2s",
              whiteSpace: "nowrap",
            }}
          >
            I Agree & Proceed →
          </button>
        </div>
      </div>
    </div>
  );
}


// Route-level gate: no jury screen renders until the declaration is accepted
// for this session, and the API independently refuses scores without it.
export function DeclarationGate({ juryName, children, declared, onAccepted }) {
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/jury/me/declaration");
      markDeclared();
      onAccepted();
    } catch {
      toast.error("Could not record your declaration. Please check your connection and try again.");
    } finally { setBusy(false); }
  };

  if (declared) return children;
  return <DeclarationModal juryName={juryName} onAccept={accept} onDecline={logout} />;
}
